import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Paper,
  Box,
  Typography,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FC, memo } from 'react';
import { PricingSetting } from 'src/entities/contractEntity';

type Props = {
  open: boolean;
  onClose: () => void;
  pricingSettings: PricingSetting[];
  contractorName?: string;
};

const PricingDetailsDialog: FC<Props> = memo(({
  open,
  onClose,
  pricingSettings,
  contractorName,
}) => {
  const getPricingMethodColor = (method: string): 'primary' | 'default' | 'info' => {
    switch (method) {
      case '個建て':
        return 'primary';
      case '車建て':
        return 'default';
      case 'トンキロ建て':
        return 'info';
      default:
        return 'default';
    }
  };

  const getUnit = (method: string) => {
    switch (method) {
      case 'トンキロ建て':
        return '/トンキロ';
      case '車建て':
        return '/台';
      case '個建て':
        return '/件';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        料金設定詳細{contractorName && ` - ${contractorName}`}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {pricingSettings.map((setting, index) => (
            <Paper key={setting.id} sx={{ p: 2 }} variant="outlined">
              {/* ヘッダー部分 */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  料金設定 {index + 1}
                </Typography>
                <Chip
                  label={setting.pricing_method}
                  color={getPricingMethodColor(setting.pricing_method)}
                  size="small"
                />
              </Box>

              {/* 基本料金セクション（トンキロ建ての場合は不要） */}
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">基本料金</Typography>
                  <Typography variant="h6">
                    {setting.base_amount != null
                      ? `¥${Number(setting.base_amount).toLocaleString()}`
                      : '—'}
                    {setting.base_amount != null && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {getUnit(setting.pricing_method)}
                      </Typography>
                    )}
                  </Typography>
                </Box>

                {/* 対象車両（車建ての場合） */}
                {setting.vehicle_types && setting.vehicle_types.length > 0 && (
                  <Box>
                    <Typography variant="body2" fontWeight="bold">対象車両</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {setting.vehicle_types.map((type, idx) => (
                        <Chip key={idx} label={type} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* 個建ての追加情報 */}
              {setting.pricing_method === '個建て' && (
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  {(setting.excess_rate !== null && setting.excess_rate !== undefined) && (
                    <Box>
                      <Typography variant="body2" fontWeight="bold">超過料金</Typography>
                      <Typography variant="body2">
                        {setting.excess_threshold}個超過時: ¥{setting.excess_rate.toLocaleString()}/個
                      </Typography>
                    </Box>
                  )}
                  {(setting.minimum_order !== null && setting.minimum_order !== undefined) && (
                    <Box>
                      <Typography variant="body2" fontWeight="bold">最低発注量</Typography>
                      <Typography variant="body2">
                        {setting.minimum_order}個未満: ¥{setting.minimum_order_fee?.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* 適用条件セクション */}
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>対象エリア</Typography>
                  {setting.area_settings && setting.area_settings.length > 0 ? (
                    setting.area_settings.map((area, idx) => (
                      <Typography key={idx} variant="body2">
                        {area.loading_area} → {area.unloading_area} ({area.area_type})
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </Box>

              </Box>

              {/* トンキロ建ての距離・重量設定テーブル */}
              {setting.pricing_method === 'トンキロ建て' &&
               setting.distance_weight_settings &&
               setting.distance_weight_settings.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>距離・重量別料金</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>距離範囲 (km)</TableCell>
                          <TableCell>重量範囲 (kg)</TableCell>
                          <TableCell align="right">料金</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {setting.distance_weight_settings.map((dw) => (
                          <TableRow key={dw.id}>
                            <TableCell>
                              {dw.distance_min ?? '-'} ～ {dw.distance_max ?? '-'}
                            </TableCell>
                            <TableCell>
                              {dw.weight_min ?? '-'} ～ {dw.weight_max ?? '-'}
                            </TableCell>
                            <TableCell align="right">
                              ¥{dw.price?.toLocaleString() ?? '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
});

PricingDetailsDialog.displayName = 'PricingDetailsDialog';

export default PricingDetailsDialog;
